import {
  Emphases,
  LabelIds,
  PaymentPlans,
  Perspectives,
  RowColors,
  RowIds,
  SectionIds,
  Stages,
} from "../consts";
import { defineRows } from "../defineRows";
import type { LabelRef, PriceFacts, PriceView } from "../types";
import { beforeGratuityValueCaption } from "./labelRules";

const upcomingChargeCaption = (facts: PriceFacts): LabelRef | undefined => {
  if (facts.upcomingChargeDate == null) return undefined;
  return {
    id: LabelIds.UpcomingChargeDate,
    args: [facts.upcomingChargeDate],
  };
};

/** Date the guest pays the venue — booking start, else event end. */
const venueDateCaption = (facts: PriceFacts): LabelRef | undefined => {
  const date = facts.bookingStart ?? facts.eventEnd;
  if (date == null) return undefined;
  return { id: LabelIds.EventDate, args: [date] };
};

const hasPriceChange = (facts: PriceFacts, view: PriceView): boolean =>
  view.stage === Stages.PostCheckout && facts.priceDifference !== null;

const hasOutstandingMoney = (facts: PriceFacts, view: PriceView): boolean =>
  view.paymentPlan !== PaymentPlans.PayInFull || hasPriceChange(facts, view);

/**
 * Section 3 — payment / payout schedule.
 *
 * Host (Rules Sheet): Payout from Peerspace → Guest spend at venue
 * (same `payout` group — no internal divider).
 * Guest (when schedule in play): Due today → Already paid →
 * Difference due today | Refund → Upcoming charge → Due at venue
 */

const paymentPayoutRows = defineRows(SectionIds.PaymentPayout, {
  payout: [
    {
      id: RowIds.PayoutFromPeerspace,
      amount: (facts) => facts.payout,
      show: (facts) => facts.payout !== null,
      perspective: Perspectives.Host,
      label: () => ({ id: LabelIds.PayoutFromPeerspace }),
      emphasis: Emphases.Strong,
      color: RowColors.Highlight,
      allowZero: true,
    },
    {
      id: RowIds.GuestSpendAtVenue,
      amount: (facts) => facts.dueAtVenue,
      show: (facts) => facts.dueAtVenue !== null && facts.dueAtVenue > 0,
      perspective: Perspectives.Host,
      label: () => ({ id: LabelIds.GuestSpendAtVenue }),
      caption: (facts) => venueDateCaption(facts),
      valueCaption: (facts) => beforeGratuityValueCaption(facts),
      color: RowColors.Highlight,
      emphasis: Emphases.Strong,
    },
  ],
  paymentSchedule: [
    {
      id: RowIds.DueToday,
      perspective: Perspectives.Guest,
      label: () => ({ id: LabelIds.DueToday }),
      amount: (facts) => facts.dueToday,
      allowZero: true,
      show: (facts, view) =>
        view.stage !== Stages.PostCheckout && hasOutstandingMoney(facts, view),
      color: RowColors.Highlight,
      emphasis: Emphases.Strong,
    },
    {
      id: RowIds.PaidToDate,
      perspective: Perspectives.Guest,
      amount: (facts) => facts.paidToDate,
      show: (facts, view) =>
        view.stage === Stages.PostCheckout && hasOutstandingMoney(facts, view),
      label: () => ({ id: LabelIds.AlreadyPaid }),
      color: RowColors.Highlight,
    },
    {
      id: RowIds.DifferenceDueToday,
      amount: (facts) =>
        facts.priceDifference != null && facts.priceDifference > 0
          ? facts.priceDifference
          : null,
      // "Difference" only makes sense against a prior payment. On an expired
      // contract whose funds were released (`paidToDate` 0) the charge is a fresh
      // "Due today", so this row stays hidden and DueToday carries the amount.
      show: (facts) =>
        facts.priceDifference != null &&
        facts.priceDifference > 0 &&
        facts.paidToDate != null &&
        facts.paidToDate > 0,
      perspective: Perspectives.Guest,
      label: () => ({ id: LabelIds.DifferenceDueToday }),
      color: RowColors.Highlight,
      emphasis: Emphases.Strong,
    },
    {
      id: RowIds.Refund,
      amount: (facts) =>
        facts.priceDifference != null && facts.priceDifference < 0
          ? Math.abs(facts.priceDifference)
          : null,
      show: (facts) =>
        facts.priceDifference != null && facts.priceDifference < 0,
      perspective: Perspectives.Guest,
      label: () => ({ id: LabelIds.Refund }),
      color: RowColors.Highlight,
      emphasis: Emphases.Strong,
    },
    {
      id: RowIds.UpcomingCharge,
      amount: (facts) => facts.upcomingCharge,
      show: (facts) => facts.upcomingCharge !== null,
      perspective: Perspectives.Guest,
      label: () => ({ id: LabelIds.UpcomingCharge }),
      caption: (facts) => upcomingChargeCaption(facts),
      color: RowColors.Highlight,
      emphasis: Emphases.Strong,
    },
    {
      id: RowIds.DueAtVenue,
      amount: (facts) => facts.dueAtVenue,
      show: (facts) => facts.dueAtVenue !== null && facts.dueAtVenue > 0,
      perspective: Perspectives.Guest,
      label: () => ({ id: LabelIds.DueAtVenue }),
      caption: (facts) => venueDateCaption(facts),
      valueCaption: (facts) => beforeGratuityValueCaption(facts),
      color: RowColors.Highlight,
      emphasis: Emphases.Strong,
    },
  ],
});

export { paymentPayoutRows };
